import { gql } from '@apollo/client';

export const SIGN_UP = gql`
  mutation SignUp($input: SignUpInput!) {
    signUp(input: $input) {
      id
      email
      cognitoId
      firstName
      lastName
      acceptedTermsAt
      emailMarketingOptIn
    }
  }
`;

export const SIGN_IN = gql`
  mutation SignIn($input: SignInInput!) {
    signIn(input: $input) {
      accessToken
      refreshToken
      idToken
      user {
        id
        email
        cognitoId
        firstName
        lastName
        displayName
        isHost
        hostStatus
        isAdmin
        myWebUrls
        shouldNotRedirectList
        phoneNumber
        isPhoneVerified
      }
    }
  }
`;

export const SIGN_OUT_MUTATION = gql`
  mutation SignOutCurrentDevice {
    signOutCurrentDevice
  }
`;

export const CONFIRM_SIGN_UP = gql`
  mutation ConfirmSignUp($input: ConfirmSignUpInput!) {
    confirmSignUp(input: $input) {
      success
      message
    }
  }
`;

export const REFRESH_TOKENS = gql`
  mutation RefreshTokens($refreshToken: String!) {
    refreshTokens(refreshToken: $refreshToken) {
      accessToken
      idToken
      refreshToken
      user {
        id
        email
        firstName
        lastName
        displayName
        isHost
        hostStatus
        isAdmin
        myWebUrls
        shouldNotRedirectList
        phoneNumber
        isPhoneVerified
      }
    }
  }
`;

export const CREATE_CROSS_SITE_SSO_CODE = gql`
  mutation CreateCrossSiteSsoCode($input: CreateCrossSiteSsoCodeInput!) {
    createCrossSiteSsoCode(input: $input) {
      code
      expiresAt
    }
  }
`;

export const EXCHANGE_CROSS_SITE_SSO_CODE = gql`
  mutation ExchangeCrossSiteSsoCode($input: ExchangeCrossSiteSsoCodeInput!) {
    exchangeCrossSiteSsoCode(input: $input) {
      accessToken
      idToken
      refreshToken
      authProvider
      sessionRefreshMode
      user {
        id
        email
        cognitoId
        firstName
        lastName
        displayName
        isHost
        hostStatus
        isAdmin
        myWebUrls
        shouldNotRedirectList
        phoneNumber
        isPhoneVerified
      }
    }
  }
`;

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      cognitoId
      firstName
      lastName
      displayName
      isHost
      hostStatus
      isAdmin
      isPlatformOwner
      isDealer
      stripeCustomerId
      phoneNumber
      isPhoneVerified
    }
  }
`;

/**
 * Paused host (D41): the host flags for the `X-Site-Host` brand. Kept apart from
 * GET_CURRENT_USER so an API without `hostListingsSuspended` never breaks sign-in.
 */
export const HOST_ACCOUNT_STATUS = gql`
  query HostAccountStatus {
    getCurrentUser {
      id
      isHost
      hostStatus
      hostListingsSuspended
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($updateUserInput: UpdateUserInput!) {
    updateUser(updateUserInput: $updateUserInput) {
      firstName
      lastName
      displayName
      isHost
      hostStatus
      isAdmin
      isDealer
    }
  }
`;

export const GET_USER = gql`
  query GetUser($id: String!) {
    user(id: $id) {
      id
      shortId
      email
      cognitoId
      firstName
      lastName
      displayName
      isHost
      hostStatus
      isAdmin
      locations {
        id
        name
        address
        city
        state
        zipCode
      }
    }
  }
`;

export const RESEND_CONFIRMATION_CODE = gql`
  mutation ResendConfirmationCode($email: String!) {
    resendConfirmationCode(email: $email) {
      success
      message
    }
  }
`;

export const FORGOT_PASSWORD = gql`
  mutation SendForgotPasswordEmail($email: String!) {
    sendForgotPasswordEmail(email: $email) {
      CodeDeliveryDetails {
        Destination
        DeliveryMedium
        AttributeName
      }
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation ConfirmForgotPassword($input: ConfirmForgotPasswordInput!) {
    confirmForgotPassword(input: $input) {
      CodeDeliveryDetails {
        Destination
        DeliveryMedium
        AttributeName
      }
    }
  }
`;

export const CREATE_OIDC_USER = gql`
  mutation CreateOidcUser(
    $email: String
    $firstName: String
    $lastName: String
    $displayName: String
  ) {
    createOidcUser(
      email: $email
      firstName: $firstName
      lastName: $lastName
      displayName: $displayName
    ) {
      id
      email
      cognitoId
      firstName
      lastName
      displayName
      isHost
      hostStatus
      isAdmin
      isDealer
      stripeCustomerId
      phoneNumber
      isPhoneVerified
    }
  }
`;

export const GET_FIREBASE_CUSTOM_TOKEN = gql`
  mutation GetFirebaseCustomToken {
    getFirebaseCustomToken
  }
`;

export const GET_OR_CREATE_CHAT = gql`
  mutation GetOrCreateChat($input: CreateChatInput!) {
    getOrCreateChat(input: $input) {
      chatId
      success
      message
    }
  }
`;

export const SEND_MESSAGE = gql`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      messageId
      success
      message
    }
  }
`;

export const MARK_CHAT_AS_READ = gql`
  mutation MarkChatAsRead($chatId: String!) {
    markChatAsRead(chatId: $chatId)
  }
`;

export const APPLY_TO_BE_HOST = gql`
  mutation ApplyToBeHost($siteUrl: String, $hostAgreement: PolicyAcceptanceInput) {
    applyToBeHost(siteUrl: $siteUrl, hostAgreement: $hostAgreement) {
      id
      hostStatus
      isHost
    }
  }
`;

export const PENDING_HOST_APPLICATIONS = gql`
  query PendingHostApplications {
    pendingHostApplications {
      id
      shortId
      email
      firstName
      lastName
      displayName
      hostStatus
      createdAt
    }
  }
`;

export const REVIEW_HOST_APPLICATION = gql`
  mutation ReviewHostApplication($userId: String!, $approved: Boolean!) {
    reviewHostApplication(userId: $userId, approved: $approved) {
      id
      hostStatus
      isHost
    }
  }
`;
